import { ModalDrawer } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { type FC, type ReactNode } from 'react'
import { PlaidMultiLinkState } from '../../../types/plaid-multi-link'
import { AccountSelection } from '../account-selection/account-selection'
import { InstitutionUpdateSelection } from '../institution-update-selection/institution-update-selection'
import { PlaidError } from '../plaid-error/plaid-error'
import { PlaidInProgress } from '../plaid-in-progress/plaid-in-progress'
import { usePlaidMultiLinkContext } from '../plaid-multi-link-context/plaid-multi-link-context'
import { PlaidSDKWrapper } from '../plaid-sdk-wrapper/plaid-sdk-wrapper'
import { PlaidSuccess } from '../plaid-success/plaid-success'

const PlaidMultiLinkModalContent: FC = () => {
  const { t } = useTranslation('features-external-accounts')
  const {
    currentState,
    selectedLinkType,
    plaidLinkToken,
    isCreatingPlaidMultiLinkToken,
    showRelink,
    isChaseBank,
    handleAccountTypeSelection,
    handleUpdateActionSelection,
    handlePlaidSuccess,
    handlePlaidError,
    handlePlaidExit,
  } = usePlaidMultiLinkContext()

  const renderContent = (): ReactNode => {
    switch (currentState) {
      // Account link type selection (Banking | Investments)
      case PlaidMultiLinkState.ACCOUNT_LINK_TYPE_SELECTION:
        return (
          <AccountSelection
            isCreatingPlaidMultiLinkToken={isCreatingPlaidMultiLinkToken}
            selectedLinkType={selectedLinkType}
            onAccountTypeSelection={handleAccountTypeSelection}
          />
        )

      // Institution management selection (Select Relink | Update | Remove Institution)
      case PlaidMultiLinkState.INSTITUTION_MANAGEMENT_SELECTION:
        return (
          <InstitutionUpdateSelection
            isChaseBank={isChaseBank}
            showRelink={showRelink}
            onUpdateActionSelection={handleUpdateActionSelection}
          />
        )

      // Initial institution linking in progress | Relink institution in progress | Update institution in progress
      case PlaidMultiLinkState.INITIAL_INSTITUTION_LINKING_IN_PROGRESS:
      case PlaidMultiLinkState.RELINKING_INSTITUTION_IN_PROGRESS:
      case PlaidMultiLinkState.UPDATING_INSTITUTION_IN_PROGRESS:
        return plaidLinkToken ? (
          <PlaidSDKWrapper
            token={plaidLinkToken}
            onSuccess={handlePlaidSuccess}
            onError={handlePlaidError}
            onExit={handlePlaidExit}
          >
            <PlaidInProgress />
          </PlaidSDKWrapper>
        ) : null

      // Success for Account Link | Relink | Remove Link
      case PlaidMultiLinkState.SUCCESS:
        return <PlaidSuccess />

      // Error for Account Link | Relink | Remove Link
      case PlaidMultiLinkState.ERROR:
        return <PlaidError />

      default:
        return null
    }
  }

  return (
    <div
      className="flex flex-col"
      data-testid="plaid-multi-link-content"
      aria-describedby="plaid-multi-link-description"
    >
      <div id="plaid-multi-link-description" className="sr-only">
        {t('plaid_multi_link.select_account_type')}
      </div>
      {renderContent()}
    </div>
  )
}

export const PlaidMultiLinkModal: FC = () => {
  const { t } = useTranslation('features-external-accounts')
  const { isOpen, modalTitle, closeModal } = usePlaidMultiLinkContext()

  return (
    <ModalDrawer
      isOpen={isOpen}
      onClose={closeModal}
      title={modalTitle ?? t('plaid_multi_link.title')}
      testId="plaid-multi-link-modal"
      ariaLabel={t('plaid_multi_link.title')}
    >
      <PlaidMultiLinkModalContent />
    </ModalDrawer>
  )
}
